import { JsonPatch, ReplaceOperation } from "json8-patch";
import { flow, types, getEnv, Instance } from "mobx-state-tree";

import { InspectionDetailsResponse } from "../../api-models/response/inspection/InspectionDetailsResponse";
import { endpoints } from "../api/ApiEndpoints";
import { ViewInspection, ViewInspectionFromAssignedProject } from "../authorization/Permissions";
import { permissionsService } from "../authorization/UserPermissionsService";
import { globalModule } from "../service-provider/GlobalModule";
import { StorageModel } from "../storage/StorageModel";
import { currentUserService } from "../user/CurrentUserStorage";
import { emptyInspection, InspectionDetails } from "./InspectionDetails";
import serializeToQueryParams from "../../helpers/QueryParams";

const currentInspectionModel = types.model({
  currentInspectionId: types.frozen<number>(),
  currentInspection: types.optional(
    types.frozen<InspectionDetails>(),
    new InspectionDetails(emptyInspection)
  ),
  releaseAlreadyTriggered: false,
  jsonPatch: types.optional(types.frozen<JsonPatch>(), []),
});

export const CurrentInspectionStorageModel = types
  .compose(currentInspectionModel, StorageModel)
  .named("CurrentInspectionStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: InspectionDetailsResponse) {
        self.currentInspection = new InspectionDetails(response);
      },
      deleteInspection: flow(function* () {
        yield getEnv(self).api.delete(self.endpoint);
      }),
      editInspectionDetails: flow(function* () {
        if (!self.jsonPatch.length) return;
        yield getEnv(self).api.patch(self.endpoint, self.jsonPatch);
        yield self.load();
      }),
      lockInspection: flow(function* () {
        yield globalModule.lockedInspectionsService.addInspection(self.currentInspectionId);
      }),
      releaseInspection: flow(function* () {
        if (!self.releaseAlreadyTriggered) {
          yield globalModule.lockedInspectionsService.removeInspection(self.currentInspectionId);
          self.releaseAlreadyTriggered = true;
        }
      }),
      reloadCurrentInspection: flow(function* () {
        yield self.load();
      }),
      validateIfInspectionCanBeMarkedAsOutdated: flow(function* () {
        const endpoint = endpoints.get("verify_inspection_can_be_marked_outdated");
        endpoint.routeParams = [self.currentInspectionId];

        return yield getEnv(self).api.get(endpoint);
      }),
      validateIfInspectionCanBeDeleted: flow(function* () {
        const endpoint = endpoints.get("verify_inspection_can_be_deleted");
        endpoint.routeParams = [self.currentInspectionId];

        return yield getEnv(self).api.get(endpoint);
      }),
      markInspectionAsRelevant: flow(function* (
        shouldAlsoMarkSameDateInspectionsAsRelevant: boolean
      ) {
        const endpoint = endpoints.get("inspection_mark_relevant");
        endpoint.routeParams = [self.currentInspectionId];
        endpoint.queryParams = serializeToQueryParams({
          shouldAlsoMarkSameDateInspectionsAsRelevant,
        });
        yield getEnv(self).api.post(endpoint);
      }),
      markOtherInspectionsAsRelevant: flow(function* () {
        const endpoint = endpoints.get("inspection_mark_others_relevant");
        endpoint.routeParams = [self.currentInspectionId];
        yield getEnv(self).api.post(endpoint);
      }),
      markInspectionAsOutdated: flow(function* () {
        const endpoint = endpoints.get("inspection_mark_outdated");
        endpoint.routeParams = [self.currentInspectionId];
        yield getEnv(self).api.post(endpoint);
      }),
      setJsonPatch(jsonPatch: JsonPatch) {
        self.jsonPatch = jsonPatch;
      },
    };
  })
  .views((self) => ({
    get canLoadItems() {
      return (
        self.currentInspectionId > 0 &&
        permissionsService.userHasOneOfThePermissions([
          ViewInspection,
          ViewInspectionFromAssignedProject,
        ])
      );
    },
    get endpoint() {
      const endpoint = endpoints.get("inspection");
      endpoint.routeParams = [self.currentInspectionId];
      return endpoint;
    },
    get isLoading() {
      return self.currentInspection.id === 0;
    },
    get inspectionIsLocked() {
      return !!self.currentInspection.lock;
    },
    get userWithLockOnInspection() {
      return self.currentInspection.lock?.owner;
    },
    get currentUserHasLockOnInspection() {
      return self.currentInspection.lock?.owner.id === currentUserService.currentUser.id;
    },
    get inspectionHasVideo() {
      return !!self.currentInspection.video;
    },
    get isRelevantOperation() {
      return self.jsonPatch.filter((x) => x.path.includes("isRelevant"))?.[0] as ReplaceOperation;
    },
  }))
  .views((self) => ({
    get inspectionIsLockedBySomeoneElse() {
      return self.inspectionIsLocked && !self.currentUserHasLockOnInspection;
    },
  }));

export type CurrentInspectionStorage = Instance<typeof CurrentInspectionStorageModel>;
